import React, { useState } from "react";
import NotificationAvatarIcon from "../../assets/Icons/NotificationAvatarIcon";
import { NOTIFICATIONS } from "../../util/constants";
import { OrdersService } from "../../api/services/orders.service";
import { UsersService } from "../../api/services/users.service";

// Start  BUG-465083
const normalizeOrderStatusEntries = (payload) => {
  const entries = payload?.data?.logs || payload?.data || payload || [];

  if (Array.isArray(entries)) {
    return entries;
  }

  return entries && typeof entries === "object" ? [entries] : [];
};
// End  BUG-465083

// Truncates text to max length with ellipsis
const truncateText = (text, maxLength = 10) => {
  if (!text) return "";
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

// Displays a single notification item with actions
export default function NotificationCard({
  item,
  onClick,
  onMarkAsRead,
  onDismiss,
}) {
  const {
    notificationId,
    notificationDetail,
    clientEntity,
    createdDate,
    category,
    isRead,
    orderId,
    analyser,
    orderGuid,
    heading,
    changes,
  } = item;

  // Truncate clientEntity and analyser to max 12 characters
  const truncatedClientEntity = truncateText(clientEntity, 12);
  const truncatedAnalyser = truncateText(analyser, 12);
  const isClientEntityTruncated = clientEntity && clientEntity.length > 12;
  const isAnalyserTruncated = analyser && analyser.length > 12;

  // State for action taken button
  const [isActionTaken, setIsActionTaken] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  // Handles action taken button click
  const handleActionTakenClick = async (e) => {
    e.stopPropagation();
    if (isActionTaken || isActionLoading) return;

    setIsActionLoading(true);
    try {
      await UsersService.markNotificationActionTaken(notificationId, orderId);
      // Start  BUG-465083
      const [openOrdersResult, actionNeededResult] = await Promise.allSettled([
        OrdersService.getOpenOrdersSummaries(orderId),
        OrdersService.getActionRequiredLogs(orderId),
      ]);

      if (openOrdersResult.status === "rejected") {
        console.error(
          "Failed to refresh open order status after action taken:",
          openOrdersResult.reason,
        );
      }

      if (actionNeededResult.status === "rejected") {
        console.error(
          "Failed to refresh action needed status after action taken:",
          actionNeededResult.reason,
        );
      }

      normalizeOrderStatusEntries(
        openOrdersResult.status === "fulfilled" ? openOrdersResult.value : [],
      );
      normalizeOrderStatusEntries(
        actionNeededResult.status === "fulfilled"
          ? actionNeededResult.value
          : [],
      );
      // End  BUG-465083
      setIsActionTaken(true);
    } catch (error) {
      console.error("Failed to mark notification action taken:", error);
    } finally {
      setIsActionLoading(false);
    }
  };

  // Truncates title to max length
  const MAX_TITLE_LENGTH = 50;
  const isTitleTruncated = heading
    ? heading.length > MAX_TITLE_LENGTH
    : notificationDetail && notificationDetail.length > MAX_TITLE_LENGTH;
  const displayTitle = isTitleTruncated
    ? `${heading ? heading.substring(0, MAX_TITLE_LENGTH) : notificationDetail.substring(0, MAX_TITLE_LENGTH)}...`
    : heading || notificationDetail;

  // Check if notification is non-clickable (Order Creation failed category)
  const isNonClickable = category?.toLowerCase() === "order creation failed";
  const isOrderCreationFailed =
    category?.toLowerCase() === "order creation failed";

  // Format changes for tooltip display
  const getChangesTooltip = () => {
    if (!changes || !Array.isArray(changes) || changes.length === 0) {
      return "No details available";
    }
    return changes
      .map((change, idx) => {
        if (typeof change === "string") {
          return change;
        }
        // Handle object format: { field, oldValue, newValue } or similar
        if (change.field || change.fieldName) {
          const fieldName = change.field || change.fieldName || "Field";
          const value = change.newValue || change.value || change.message || "";
          return `${fieldName}: ${value}`;
        }
        if (change.message) {
          return change.message;
        }
        return JSON.stringify(change);
      })
      .join("\n");
  };

  // Handles notification card click
  const handleClick = () => {
    if (onClick && !isNonClickable) {
      onClick(notificationId, isRead, orderId, orderGuid, changes, category);
    }
  };

  const date = createdDate ? new Date(createdDate) : null;
  const formatted = date
    ? date.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  return (
    <div
      className={`notif-item ${isOrderCreationFailed ? "order-creation-failed" : ""}`}
      onClick={handleClick}
      style={{ cursor: onClick && !isNonClickable ? "pointer" : "default" }}
      role={isNonClickable ? undefined : "button"}
      tabIndex={isNonClickable ? -1 : 0}
      title={isOrderCreationFailed ? getChangesTooltip() : undefined}
      onKeyDown={(e) => {
        if (
          onClick &&
          !isNonClickable &&
          (e.key === "Enter" || e.key === " ")
        ) {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* Card content wrapper - shifts left on hover */}
      <div className="notif-card-content">
        <div className="notif-icon" aria-hidden="true">
          <NotificationAvatarIcon />
        </div>

        <div className="notif-info">
          {/* Title and Category in same row */}
          <div className="notif-title-row">
            <div
              className="notif-item-title"
              title={
                isTitleTruncated
                  ? heading
                    ? heading
                    : notificationDetail
                  : undefined
              }
            >
              {displayTitle}
            </div>
            {(category === "Action Needed" ||
              category === "Order Creation Failed" ||
              category === "Validation Errors") &&
              (isActionTaken ? (
                <span className="notif-action-taken-text">
                  {NOTIFICATIONS.ACTION_TAKEN}
                </span>
              ) : (
                <button
                  className="notif-action-taken-btn"
                  onClick={handleActionTakenClick}
                  disabled={isActionLoading}
                >
                  {isActionLoading
                    ? "Processing..."
                    : NOTIFICATIONS.ACTION_TAKEN}
                </button>
              ))}
          </div>
          {(orderId || clientEntity || analyser) && (
            <div className="notif-item-sub">
              {orderId && <span>AX-{orderId}</span>}
              {orderId && clientEntity && <span> | </span>}
              {clientEntity && (
                <span
                  title={isClientEntityTruncated ? clientEntity : undefined}
                  className={isClientEntityTruncated ? "truncated-text" : ""}
                >
                  {truncatedClientEntity}
                </span>
              )}
              {(orderId || clientEntity) && analyser && <span> | </span>}
              {analyser && (
                <span
                  title={isAnalyserTruncated ? analyser : undefined}
                  className={isAnalyserTruncated ? "truncated-text" : ""}
                >
                  {truncatedAnalyser}
                </span>
              )}
            </div>
          )}
          <div className="notif-bottom">
            {formatted && <div className="notif-item-meta">{formatted}</div>}
            {/* Action Taken button only for Action Needed category */}
            {category && <div className="notif-item-category">{category}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
